import { useCallback, useMemo } from 'react'

import {
  CMS_MICROSITE_MESSAGE,
  CMS_MICROSITE_MESSAGE_KEY,
  ESSENTIALS_CONTENT_CATEGORY,
  ESSENTIALS_EVENTS_PAGE,
  EssentialsContentData,
  ProviderProgramTaxonomy,
  useNavigateToEssentialsContentProps,
} from '@lyrahealth-inc/shared-app-logic'

type navigateToEssentialsHomepageWidgetParams = {
  slug?: string
  category?: ESSENTIALS_CONTENT_CATEGORY
  path?: string
}

/**
 * Get post message event handlers based on the cms microsite's metadata
 */
export const useGetCMSMicrositeMessageHandlers = ({
  eventsList,
  isLoggedIn,
  navigateToCareOptions,
  navigateToWellnessCheckIn,
  navigateToCriticalIncidentSupportToolkit,
  navigateToEssentials,
  navigateToEssentialsHomepageWidget,
  navigateToWebinar,
  showRegistrationModal,
  navigateToRegistration,
  clickSelfCareContent,
  openExternalLink,
  navigateToProviderBooking,
  openCareNavigatorModal,
}: {
  eventsList: { key: CMS_MICROSITE_MESSAGE; params?: Dict }[]
  isLoggedIn: boolean
  navigateToCareOptions: () => void
  navigateToWellnessCheckIn?: () => void
  navigateToCriticalIncidentSupportToolkit?: () => void
  navigateToEssentials: ({
    navigateToEssentialsHookProps,
  }: {
    navigateToEssentialsHookProps: useNavigateToEssentialsContentProps
  }) => void
  navigateToEssentialsHomepageWidget?: (params: navigateToEssentialsHomepageWidgetParams) => void
  navigateToWebinar: ({ webinarLink }: { webinarLink: string }) => void
  showRegistrationModal?: (messageKey: CMS_MICROSITE_MESSAGE) => void
  navigateToRegistration?: () => void
  clickSelfCareContent?: ({
    contentData,
    contentSlug,
  }: {
    contentData?: EssentialsContentData
    contentSlug?: string
  }) => void
  openExternalLink: ({ link }: { link: string }) => void
  navigateToProviderBooking?: ({
    providerId,
    ctpo,
    newTab,
  }: {
    providerId: string
    ctpo?: ProviderProgramTaxonomy
    newTab?: boolean
  }) => void
  openCareNavigatorModal: () => void
}): { [key in CMS_MICROSITE_MESSAGE]?: () => void } => {
  const getPostMessageEventData = (message: MessageEvent) => {
    // Nested under data for web, directly in message for mobile
    return message?.data || message
  }

  const goToWellnessCheckIn = useCallback(() => {
    navigateToWellnessCheckIn && navigateToWellnessCheckIn()
  }, [navigateToWellnessCheckIn])

  const goToEssentials = useCallback(
    ({
      message,
      category = ESSENTIALS_CONTENT_CATEGORY.ARTICLE,
      slug,
      path,
    }: {
      message: MessageEvent
      category?: ESSENTIALS_CONTENT_CATEGORY
      slug?: string
      path?: string
    }) => {
      if (isLoggedIn) {
        const eventData = getPostMessageEventData(message)
        const navigateToEssentialsHookProps = JSON.parse(eventData?.mixPanelData?.navigateToEssentialsHookProps ?? '{}')

        navigateToEssentials({
          navigateToEssentialsHookProps: {
            ...navigateToEssentialsHookProps,
            loggedIn: isLoggedIn,
            ...(slug && { slug, category }),
            ...(path && !slug && { subPath: path }),
          },
        })
      } else {
        navigateToEssentialsHomepageWidget && navigateToEssentialsHomepageWidget({ slug, category, path })
      }
    },
    [isLoggedIn, navigateToEssentials, navigateToEssentialsHomepageWidget],
  )

  const registerWebinar = useCallback(
    ({
      messageKey,
      webinarLink = ESSENTIALS_EVENTS_PAGE,
    }: {
      webinarLink?: string
      messageKey: CMS_MICROSITE_MESSAGE
    }) => {
      if (isLoggedIn) {
        navigateToWebinar({ webinarLink })
      } else {
        showRegistrationModal && showRegistrationModal(messageKey)
      }
    },
    [isLoggedIn, navigateToWebinar, showRegistrationModal],
  )

  const goToRegistration = useCallback(() => {
    if (!isLoggedIn) {
      navigateToRegistration && navigateToRegistration()
    } else {
      navigateToCareOptions()
    }
  }, [isLoggedIn, navigateToCareOptions, navigateToRegistration])

  const handleClickSelfCareContent = useCallback(
    ({ message }: { message: MessageEvent }) => {
      const eventData = getPostMessageEventData(message)
      clickSelfCareContent &&
        clickSelfCareContent({
          contentData: eventData?.contentData, // Event + content data sent automatically when using goodness's essentials carousel
          contentSlug: eventData?.mixPanelData?.content_slug, // If not using the carousel, can manually attach the event + content_slug in the CMS
        })
    },
    [clickSelfCareContent],
  )

  const goToProviderBooking = useCallback(
    ({ message }: { message: MessageEvent }) => {
      const eventData = getPostMessageEventData(message)
      let ctpo: ProviderProgramTaxonomy | undefined
      try {
        const ctpoString = eventData?.mixPanelData?.ctpo
        ctpo = ctpoString ? JSON.parse(ctpoString) : undefined
      } catch (error) {
        console.error('Failed to parse ctpo from post message:', error)
        ctpo = undefined
      }
      // todo: See if goodness can support extra props without nesting them under mixPanelData
      let newTab = false
      try {
        const newTabString = eventData?.mixPanelData?.newTab
        newTab = newTabString ? JSON.parse(newTabString.toLowerCase()) : false // "true" / "false"
      } catch (error) {
        console.error('Failed to parse newTab from post message:', error)
        newTab = false
      }

      navigateToProviderBooking &&
        navigateToProviderBooking({
          providerId: eventData?.mixPanelData?.provider_id,
          ctpo,
          newTab,
        })
    },
    [navigateToProviderBooking],
  )

  const handleOpenExternalLink = useCallback(
    ({ message, link }: { message: MessageEvent; link: string }) => {
      const eventData = getPostMessageEventData(message)
      openExternalLink({ link: eventData?.mixPanelData?.link ?? link })
    },
    [openExternalLink],
  )

  const handlers = useMemo(() => {
    return {
      [CMS_MICROSITE_MESSAGE_KEY.NAVIGATE_TO_FORKED_TRIAGE]: navigateToCareOptions,
      [CMS_MICROSITE_MESSAGE_KEY.NAVIGATE_TO_CRITICAL_INCIDENT_SUPPORT_TOOLKIT]:
        navigateToCriticalIncidentSupportToolkit,
      [CMS_MICROSITE_MESSAGE_KEY.NAVIGATE_TO_WELLNESS_CHECK_IN]: goToWellnessCheckIn,
      [CMS_MICROSITE_MESSAGE_KEY.NAVIGATE_TO_ESSENTIALS]: goToEssentials,
      [CMS_MICROSITE_MESSAGE_KEY.REGISTER_WEBINAR]: registerWebinar,
      [CMS_MICROSITE_MESSAGE_KEY.NAVIGATE_TO_WEBINAR_EVENTS]: registerWebinar,
      [CMS_MICROSITE_MESSAGE_KEY.NAVIGATE_TO_REGISTRATION]: goToRegistration,
      [CMS_MICROSITE_MESSAGE_KEY.CLICK_SELF_CARE_CONTENT]: handleClickSelfCareContent,
      [CMS_MICROSITE_MESSAGE_KEY.OPEN_EXTERNAL_LINK]: handleOpenExternalLink,
      [CMS_MICROSITE_MESSAGE_KEY.NAVIGATE_TO_PROVIDER_BOOKING]: goToProviderBooking,
      [CMS_MICROSITE_MESSAGE_KEY.OPEN_CONTACT_CARE_NAVIGATOR_MODAL]: openCareNavigatorModal,
    }
  }, [
    goToEssentials,
    goToRegistration,
    goToWellnessCheckIn,
    handleClickSelfCareContent,
    navigateToCareOptions,
    navigateToCriticalIncidentSupportToolkit,
    registerWebinar,
    handleOpenExternalLink,
    goToProviderBooking,
    openCareNavigatorModal,
  ])

  return useMemo(() => {
    return eventsList
      .map((item) => {
        return {
          [item.key]: (message: MessageEvent) => {
            handlers[item.key]?.({ message, ...(item.params as any), messageKey: item.key })
          },
        }
      })
      .reduce((result, currentEventAndHandler) => {
        return { ...result, ...currentEventAndHandler }
      }, {})
  }, [eventsList, handlers])
}
